import {
	DEFAULT_SEPARATOR_ICON,
	useBreadcrumbsState,
} from '@dealstack/frontend/shared/components/Breadcrumbs/useBreadcrumbs';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { memo } from 'react';
import { NavLink } from 'react-router-dom';
import styled from 'styled-components';
import { Flex, Text, TextProps, FlexProps } from '@mantine/core';

interface Props {
	leftMargin?: string;
	breadcrumbTextVariant?: TextProps['variant'];
	fontWeight?: TextProps['fw'];
	textColor?: string;
	activeItemColor?: string;
}

const BreadcrumbsV2 = ({
	leftMargin = '1rem',
	breadcrumbTextVariant = 'h6',
	fontWeight = 600,
	textColor,
	activeItemColor,
}: Props) => {
	const { breadcrumbs, separator } = useBreadcrumbsState();
	const visibleBreadcrumbs = breadcrumbs?.filter((breadcrumb) => !breadcrumb.hideInAppHeader);

	return (
		<BreadcrumbsWrapper
			align="center"
			ml={leftMargin}
			$textColor={textColor}
			$activeItemColor={activeItemColor}
		>
			{visibleBreadcrumbs?.map((breadcrumb, index) => {
				const isFirstBreadcrumb = index === 0;
				return (
					<Flex align="center" key={index}>
						{(separator.skipFirst ? !isFirstBreadcrumb : true) && (
							<Icon
								key={index}
								variant={separator.icon ?? DEFAULT_SEPARATOR_ICON}
								size="vlg"
							/>
						)}
						{breadcrumb.element ? (
							breadcrumb.element
						) : (
							<BreadcrumbLink to={breadcrumb.url} key={breadcrumb.id}>
								<Text
									variant={breadcrumbTextVariant}
									fw={fontWeight}
									lineClamp={1}
									truncate
									title={isFirstBreadcrumb ? undefined : breadcrumb.name}
									style={isFirstBreadcrumb ? { whiteSpace: 'nowrap' } : {}}
								>
									{breadcrumb.name}
								</Text>
							</BreadcrumbLink>
						)}
					</Flex>
				);
			})}
		</BreadcrumbsWrapper>
	);
};

export const BreadcrumbLink = styled(NavLink)`
	display: flex;
	align-items: center;
	cursor: pointer;
	padding: 0.5rem;
	color: inherit;
`;

export const BreadcrumbsWrapper = styled(Flex)<
	FlexProps & { $textColor?: string; $activeItemColor?: string }
>`
	color: ${({ theme, $activeItemColor }) => $activeItemColor ?? theme.v3.colors.dark[2]};

	& > *:not(:last-child) {
		color: ${({ theme, $textColor }) => $textColor ?? theme.v3.colors.blue[6]};
	}
`;

const Memoized = memo(BreadcrumbsV2);

export { Memoized as BreadcrumbsV2 };
